import React, { useState, useMemo, useEffect, useCallback, useContext } from "react";
import {
    Box,
    Avatar,
    Typography,
    Drawer,
    IconButton,
    Tooltip,
    Popover,
    Stack,
} from "@mui/material";

import {
    Logout,
    ChevronLeft,
    ChevronRight,
} from "@mui/icons-material";

import * as ReactRouterDOM from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";

import { useBranding } from "../../hooks/useBranding"

import { authApi } from "../../api/authApi";
import { menuConfig } from "../../config/menuConfig";
import { useAuth } from "../../contexts/AuthContext";

import CMNB from "../../assets/images/merubot_logo.png";
import CMSNB from "../../assets/images/merubot_icon.png";

const DRAWER_WIDTH = {
    expanded: 240,
    collapsed: 72,
};

/* ------------------------------------------------------------------
   LOGOUT LOADER
------------------------------------------------------------------ */
const LogoutLoader = () => {
    const theme = useTheme();
    const MESSAGES = ["Securing session...", "Clearing workspace...", "See you soon! 👋"];
    const [msgIndex, setMsgIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setMsgIndex((prev) => (prev + 1) % MESSAGES.length);
        }, 700);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box
            sx={{
                position: "fixed", inset: 0, zIndex: 9999, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 1.5,
                bgcolor: alpha(theme.palette.background.default, 0.85),
                backdropFilter: "blur(12px)", fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
        >
            <Box
                sx={{
                    width: 36, height: 36, borderRadius: "50%",
                    border: `2px solid ${theme.palette.divider}`,
                    borderTop: `2px solid ${theme.palette.primary.main}`,
                    animation: "spin 0.8s linear infinite",
                    "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                }}
            />
            <Typography key={msgIndex} sx={{ color: theme.palette.text.primary, fontSize: "0.72rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
                {MESSAGES[msgIndex]}
            </Typography>
        </Box>
    );
};

/* ------------------------------------------------------------------
   SIDEBAR CONTENT (Themed & Elevated)
------------------------------------------------------------------ */
const SidebarContent = ({
    mobileOpen,
    handleDrawerToggle,
    userType,
    // plan,
    isAuthenticated
}) => {
    const theme = useTheme();
    const { logout } = useAuth();
    const isDark = theme.palette.mode === "dark";
    const navigate = ReactRouterDOM.useNavigate();
    const location = ReactRouterDOM.useLocation();

    const [collapsed, setCollapsed] = useState(false);
    const [profile, setProfile] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [loggingOut, setLoggingOut] = useState(false);

    const drawerWidth = collapsed ? DRAWER_WIDTH.collapsed : DRAWER_WIDTH.expanded;

    const fetchProfile = useCallback(async () => {
        try {
            const res = await authApi.profile();
            setProfile(res.data || {});
        } catch (error) {
            // console.error("Failed to fetch profile:", error);
            handleLogout()
        }
    }, []);

    const { logoUrl, handleContextLogout } = useBranding();

    useEffect(() => {
        if (isAuthenticated) {
            fetchProfile();
        }
        else {
            handleLogout()
        }
    }, [fetchProfile]);


    const menuItems = useMemo(() => {
        if (isAuthenticated) {
            const role = userType?.toLowerCase();
            const screenList = menuConfig[role] || [];
            return screenList;
        }
        else {
            return []
        }

    }, [userType]);

    const handleProfileClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleLogout = async () => {
        try {
            setLoggingOut(true);
            await authApi.logout();
            handleContextLogout();
            logout();
        } catch (error) {
            // console.error("Logout failed:", error);
        } finally {
            setAnchorEl(null);
            navigate("/login", { replace: true });
        }
    };

    const drawerContent = (
        <Box
            sx={{
                width: drawerWidth,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "width 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)",
                bgcolor: theme.palette.background.paper,
                borderRight: `1px solid ${theme.palette.divider}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
        >
            {/* ─── DYNAMIC LOGO ─── */}
            <Box
                sx={{
                    py: 2.5,
                    px: collapsed ? 0 : 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    flexShrink: 0,
                    minHeight: 68,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                        src={collapsed ? CMSNB : (logoUrl || CMNB)}
                        alt="Logo"
                        style={{
                            maxHeight: collapsed ? "24px" : "30px",
                            maxWidth: collapsed ? "24px" : "130px",
                            objectFit: "contain",
                            display: "block",
                            filter: isDark ? "brightness(0.9) contrast(1.1)" : "none",
                        }}
                    />
                </Box>
            </Box>

            {/* ─── MENU ─── */}
            <Box sx={{ flex: 1, overflowY: "auto", px: collapsed ? 1.2 : 1.5, pt: 0.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {menuItems?.map((item) => {
                        const isActive = location.pathname === item.path;

                        const menuItemRender = (
                            <Box
                                onClick={() => navigate(item.path)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: collapsed ? "center" : "flex-start",
                                    height: 38,
                                    px: collapsed ? 0 : 1.5,
                                    borderRadius: "10px",
                                    cursor: "pointer",
                                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                    position: "relative",
                                    transition: "all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1)",
                                    "&:hover": {
                                        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.text.primary, 0.04),
                                        transform: collapsed ? "none" : "translateX(4px)",
                                        color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                                    },
                                    "&::before": isActive && !collapsed ? {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        height: "50%",
                                        width: "3px",
                                        bgcolor: theme.palette.primary.main,
                                        borderRadius: "0 4px 4px 0",
                                    } : {},
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "inherit",
                                        mr: collapsed ? 0 : 1.5,
                                        "& svg": { fontSize: 18, transition: "color 0.2s ease" },
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                {!collapsed && (
                                    <Typography
                                        sx={{
                                            fontSize: "0.75rem",
                                            fontWeight: isActive ? 700 : 500,
                                            fontFamily: "inherit",
                                            letterSpacing: "-0.01em",
                                        }}
                                    >
                                        {item.text}
                                    </Typography>
                                )}
                            </Box>
                        );

                        return collapsed ? (
                            <Tooltip key={item.text} title={item.text} placement="right" arrow componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.text.primary, color: theme.palette.background.paper, fontWeight: 600, fontSize: "0.65rem", fontFamily: "inherit" } } }}>
                                {menuItemRender}
                            </Tooltip>
                        ) : (
                            <Box key={item.text}>{menuItemRender}</Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ─── BOTTOM PROFILE PILL ─── */}
            <Box sx={{ p: collapsed ? 1.2 : 1.5, pb: 2.5 }}>
                {!collapsed ? (
                    <Box
                        onClick={handleProfileClick}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            borderRadius: "12px",
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.paper,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                bgcolor: alpha(theme.palette.text.primary, 0.02),
                                borderColor: alpha(theme.palette.text.primary, 0.2),
                                boxShadow: isDark ? "none" : "0 4px 12px rgba(0,0,0,0.03)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, overflow: "hidden" }}>
                            <Avatar
                                sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                {profile?.full_name?.[0]?.toUpperCase() || "U"}
                            </Avatar>
                            <Box sx={{ overflow: "hidden" }}>
                                <Typography noWrap sx={{ fontSize: "0.72rem", fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                                    {profile?.full_name || "User"}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: "0.65rem", fontWeight: 500, color: theme.palette.text.secondary }}>
                                    {profile?.company_name || (userType ? `${userType.charAt(0).toUpperCase()}${userType.slice(1)}` : "")}
                                </Typography>
                            </Box>
                        </Box>
                        {/* 👇 Restored the setCollapsed click handler inside an IconButton 👇 */}
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setCollapsed(true);
                            }}
                            sx={{ color: theme.palette.text.secondary, p: 0.3 }}
                        >
                            <ChevronLeft sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                ) : (
                    <Stack alignItems="center" spacing={1.2}>
                        <Tooltip title={profile?.full_name || "User"} placement="right" arrow componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.text.primary, color: theme.palette.background.paper, fontWeight: 500, fontSize: "0.65rem", fontFamily: "inherit" } } }}>
                            <Avatar
                                onClick={handleProfileClick}
                                sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: theme.palette.primary.main,
                                    cursor: "pointer",
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                    color: theme.palette.primary.contrastText,
                                    transition: "all 0.2s ease",
                                    "&:hover": { transform: "scale(1.08)" },
                                }}
                            >
                                {profile?.full_name?.[0]?.toUpperCase() || "U"}
                            </Avatar>
                        </Tooltip>
                        <IconButton size="small" onClick={() => setCollapsed(false)} sx={{ color: theme.palette.text.secondary, border: `1px solid ${theme.palette.divider}`, p: 0.3 }}>
                            <ChevronRight sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Stack>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {loggingOut && <LogoutLoader />}

            <Box sx={{ display: { xs: "none", sm: "block" }, width: drawerWidth, flexShrink: 0, transition: "width 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)" }}>
                {drawerContent}
            </Box>

            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", sm: "none" },
                    "& .MuiDrawer-paper": { width: DRAWER_WIDTH.expanded, boxSizing: "border-box", borderRight: "none", bgcolor: "transparent" },
                }}
            >
                {drawerContent}
            </Drawer>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        width: 210,
                        borderRadius: "12px",
                        p: 1.2,
                        boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 10px 30px -5px rgba(0,0,0,0.08)",
                        border: `1px solid ${theme.palette.divider}`,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        ml: 1.5,
                        mb: 1,
                        backdropFilter: "blur(12px)",
                        bgcolor: alpha(theme.palette.background.paper, 0.85),
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: theme.palette.primary.main, fontSize: "0.85rem", fontWeight: 700, color: theme.palette.primary.contrastText }}>
                        {profile?.full_name?.[0]?.toUpperCase() || "U"}
                    </Avatar>
                    <Box sx={{ overflow: "hidden" }}>
                        <Typography noWrap sx={{ fontWeight: 800, fontSize: "0.75rem", color: theme.palette.text.primary, letterSpacing: "-0.01em" }}>
                            {profile?.full_name || "User"}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: "0.62rem", color: theme.palette.text.secondary }}>
                            {profile?.email}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    onClick={handleLogout}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        p: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        color: theme.palette.error.main,
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08), transform: "scale(0.98)" },
                    }}
                >
                    <Logout sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Log out securely</Typography>
                </Box>
            </Popover>
        </>
    );
};

const Sidebar = (props) => {
    const NavigationContext = ReactRouterDOM.UNSAFE_NavigationContext;
    const context = useContext(NavigationContext || React.createContext(null));

    if (!context || !context.navigator) {
        const MemoryRouter = ReactRouterDOM.MemoryRouter || ReactRouterDOM.BrowserRouter;
        return (
            <MemoryRouter>
                <SidebarContent {...props} />
            </MemoryRouter>
        );
    }

    return <SidebarContent {...props} />;
};

export default Sidebar;

